<template>
	<b-modal
		v-model="payrollModalOption.visible"
		:title="titleModal"
		header-class="header-class-modal-doc-package"
		hide-header-close
		no-close-on-backdrop
		no-close-on-esc
		@ok.prevent="handleSave"
		@cancel.prevent="emitEventClose"
		:size="payrollModalOption.locale ? 'xs' : 'lg'"
		dialog-class="dialog-custom-payroll-config"
		modal-class="mui-animation"
		:fade="false"
	>
		<div v-if="payrollModalOption.locale">{{ FormMSG(13, 'This action will validate the current salary grid internally without sending it to GHS.') }}</div>
		<div v-else class="d-flex flex-column flex-1 w-100 gap-25">
			<div v-if="informationLabelContract">
				<b-alert show dismissible>
					{{ informationLabelContract }}
				</b-alert>
			</div>
			<div class="d-flex flex-row">
				<div class="w-50">
					<b-form-radio
						v-model="typeContract"
						class="form-radio-check"
						:value="FIMALAC_CONTRACT_TYPE.LONG"
						name="type-contract"
						:disabled="isCurrentContract.SHORT"
					>
						{{ FormMSG(7, 'Long contract') }}
					</b-form-radio>
				</div>
				<div class="w-50">
					<b-form-radio
						v-model="typeContract"
						class="form-radio-check"
						:value="FIMALAC_CONTRACT_TYPE.SHORT"
						name="type-contract"
						:disabled="isCurrentContract.LONG"
					>
						{{ FormMSG(5, 'Short contract') }}
					</b-form-radio>
				</div>
			</div>
			<div class="d-flex flex-row flex-1 w-100">
				<div class="w-50 check-disabled">
					<b-form-checkbox
						id="lock-planning"
						v-model="lockPlanning"
						name="lock-planning"
						:value="true"
						:unchecked-value="false"
						:disabled="isActiveLockPlanning"
						:class="{ 'disabled-checkbox': isActiveLockPlanning }"
					>
						{{ FormMSG(6, 'Lock planning until:') }}
					</b-form-checkbox>
				</div>
				<div class="w-50">
					<v-date-picker
						v-model="payrollDate"
						:available-dates="{
							start: new Date(isDatePlanning),
							end: null
						}"
						:locale="lang"
						:masks="masks"
						@dayclick="handleChangeDate"
						:model-config="{ type: 'string', mask: masks, timezone: 'UTC' }"
					>
						<template v-slot="{ inputValue, togglePopover }">
							<b-input-group>
								<b-form-input :value="inputValue" class="br-none" readonly />
								<b-input-group-append>
									<b-input-group-text class="cursor-pointer bg-color-white input-group-text-bg-white" @click="togglePopover()">
										<component :is="getLucideIcon('Calendar')" color="rgba(6, 38, 62, .6)" :size="18" :stroke-width="2.25" />
									</b-input-group-text>
								</b-input-group-append>
							</b-input-group>
						</template>
					</v-date-picker>
				</div>
			</div>
		</div>

		<template #modal-footer>
			<div class="w-100 d-flex justify-content-end align-items-center">
				<b-button size="md" variant="custom-outline-gray" style="margin-top: 5px" class="w-138-px mr-3" @click="closeModalPayrollConfig" block>
					{{ FormMSG(2, 'Cancel') }}
				</b-button>
				<b-button size="md" variant="primary" class="w-138-px" @click="handleSave" block>
					<div class="d-flex justify-content-center align-items-center">
						<div style="margin-top: 1px">{{ FormMSG(3, 'Send') }}</div>
					</div>
				</b-button>
			</div>
		</template>

		<b-modal
			title="Information"
			class="modal-danger"
			v-model="openModalInfoPayroll"
			@cancel.prevent="closeInfoModalPayroll"
			no-close-on-backdrop
			ok-variant="danger"
			ok-only
			modal-class="mui-animation"
			:fade="false"
		>
			<div v-html="info"></div>
			<template #modal-footer>
				<div class="w-100">
					<b-button @click="closeInfoModalPayroll" size="sm" class="float-right"> OK </b-button>
				</div>
			</template>
		</b-modal>
	</b-modal>
</template>

<script>
import languageMessages from '@/mixins/languageMessages';
import globalMixin from '@/mixins/global.mixin';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import { exportTsDayDetails } from '@/cruds/timesheet.crud';
import { formatDatesInString, STATUS_VALIDATE_PLANNING, FIMALAC_CONTRACT_TYPE } from '@/shared/constants';

export default {
	name: 'PayrollConfig',
	props: {
		contractType: {
			type: Number,
			required: false,
			default: 0
		},
		payrollModalOption: {
			type: Object,
			required: false,
			default: {
				visible: false,
				locale: true
			}
		},
		dateDefaultSelect: {
			type: Object,
			required: false
		},
		planningOption: {
			type: Object || null,
			required: false,
			default: () => []
		},
		statusPlanningValidateAll: {
			type: Number,
			required: false
		}
	},

	mixins: [languageMessages, globalMixin],

	components: {
		'v-date-picker': DatePicker
	},

	data() {
		return {
			FIMALAC_CONTRACT_TYPE,
			payrollDate: new Date(),
			lockPlanning: false,
			typeContract: 0,
			informationLabelContract: null,
			masks: {
				input: 'DD/MM/YYYY'
			},
			openModalInfoPayroll: false,
			info: ''
		};
	},

	computed: {
		titleModal() {
			return this.payrollModalOption.locale ? this.FormMSG(12, 'Salary Grid Approval Without GHS Export') : this.FormMSG(1, 'Send to payroll option');
		},
		isCurrentContract() {
			const { contractType, FIMALAC_CONTRACT_TYPE } = this;
			const { SHORT, LONG } = FIMALAC_CONTRACT_TYPE;
			const isValid = [SHORT, LONG].includes(contractType);
			const type = isValid ? contractType : LONG;
			this.typeContract = type;
			this.informationLabelContract = null;

			if (isValid) {
				const labelText = this.FormMSG(9, 'The current contract active is');
				const labelShort = this.FormMSG(10, 'Short');
				const labelLong = this.FormMSG(11, 'Long');
				this.informationLabelContract = `${labelText} ${type === SHORT ? labelShort : labelLong}`;
				return {
					SHORT: type === SHORT,
					LONG: type === LONG
				};
			}
			return { SHORT: false, LONG: false };
		},
		isDatePlanning() {
			if (this.dateDefaultSelect) {
				return formatDatesInString('01/' + this.dateDefaultSelect.value, 'YYYY-MM-DD');
			}
			return formatDatesInString(new Date(), 'YYYY-MM-DD');
		},
		isActiveLockPlanning() {
			const status = this.statusPlanningValidateAll;
			if (status) {
				const { CLOSED_PAYROLL } = STATUS_VALIDATE_PLANNING;
				const checkED = [CLOSED_PAYROLL].includes(status);
				return !checkED;
			}
			return true;
		}
	},

	watch: {
		dateDefaultSelect: {
			handler(newVal) {
				if (newVal) {
					const [month, year] = this.dateDefaultSelect.value.split('/');
					const lastDay = `${new Date(year, month, 0).getDate()}`.padStart(2, '0');
					this.payrollDate = formatDatesInString(`${lastDay}/` + this.dateDefaultSelect.value, 'YYYY-MM-DD');
					return newVal;
				}
				return newVal;
			},
			immediate: true,
			deep: true
		}
	},

	async created() {},

	methods: {
		closeInfoModalPayroll() {
			this.openModalInfoPayroll = false;
			this.emitEventClose();
		},
		handleChangeDate(payload) {
			this.payrollDate = payload.date;
		},
		async handleSave() {
			const onSaveAuthorized = async () => {
				const forPaieProcessing = !this.payrollModalOption.locale;
				const date = formatDatesInString(this.payrollDate, 'YYYY-MM-DD') + 'T00:00:00Z';
				const typeContract = this.typeContract;
				const lockPlanning = this.lockPlanning;
				const labelTypeSend = `(${this.payrollModalOption.locale ? 'locally' : 'GHS'})`;
				this.$emit('on-show-is-loading', true);
				try {
					const response = await exportTsDayDetails(date, typeContract, lockPlanning, forPaieProcessing);
					this.openModalInfoPayroll = true;
					if (response) {
						if (response.toString().includes('<head>')) {
							this.info = response;
						} else {
							this.info = `<div>${this.FormMSG(8, 'Send to payroll with success')} ${` ${labelTypeSend}`}</div>`;
						}
					} else {
						this.info = `<div>${this.FormMSG(8, 'Send to payroll with success')} ${` ${labelTypeSend}`}</div>`;
					}
				} catch (error) {
					console.error('Error while sending to payroll:', error);
					this.$bvToast.toast(`Unexpected error while sending ${labelTypeSend}`, {
						title: 'Exception',
						variant: 'danger',
						solid: true,
						toaster: 'b-toaster-top-center',
						autoHideDelay: 3000
					});
				} finally {
					this.$emit('on-show-is-loading', false);
				}
			};

			const mapMessage =
				{
					[true]: this.FormMSG(14, 'Are you sure you just want to validate the salary grid locally?'),
					[false]: this.FormMSG(15, 'Are you sure you want to send the salary grid to GHS?')
				}[this.payrollModalOption.locale] || '';
			return this.$bvModal
				.msgBoxConfirm(mapMessage, {
					title: this.FormMSG(16, 'Confirm'),
					size: 'sm',
					buttonSize: 'sm',
					okVariant: 'danger',
					okTitle: this.FormMSG(17, 'Yes'),
					cancelTitle: this.FormMSG(18, 'No'),
					footerClass: 'p-2',
					hideHeaderClose: false,
					centered: false,
					modalClass: 'mui-animation'
				})
				.then((v) => {
					if (!v) return false;
					else onSaveAuthorized();
				});
		},
		emitEventClose() {
			this.$emit('actionModal');
			this.$emit('is-success-to-refresh');
			this.info = '';
		},
		closeModalPayrollConfig() {
			this.openModalInfoPayroll = false;
			this.$emit('actionModal');
			this.info = '';
		}
	}
};
</script>
<style scoped lang="scss">
.dialog-custom-payroll-config {
	width: 40svw !important;
}
.gap-25 {
	gap: 25px;
}
</style>
<style lang="scss">
%custom-disabled-style {
	color: #fff !important;
	background: #a1acbb !important;
	cursor: pointer !important;
}

.check-disabled,
.form-radio-check {
	.custom-control-input:hover ~ .custom-control-label::after {
		cursor: pointer !important;
	}
	.custom-control-input:disabled ~ .custom-control-label::before {
		@extend %custom-disabled-style;
	}
}
</style>
